/**
 * THE ONLY FILE IN THIS APP ALLOWED TO SAY "ingreso" OR "revenue".
 *
 * `scripts/eslint-money-gate.mjs` fails CI on those words anywhere else. Here
 * they appear exclusively inside the sentences that explain why a number is
 * *not* one of those things.
 *
 * ── WHY SPANISH, AND WHY NO i18n FRAMEWORK ─────────────────────────────────
 * The truth-telling vocabulary ALREADY EXISTS in Spanish and is already
 * load-bearing: queries/negocio/*.sql in the product repo says
 * "`valor_lista_usd` NO es ingreso" in so many words. Re-translating it is
 * precisely the operation during which "valor de lista" becomes "revenue".
 *
 * Scope: user-visible copy in Spanish; routes, identifiers, domain folder names,
 * API fields and query keys in English, because those are identifiers and must
 * match the Rust models and the SQL columns. With the copy centralised here,
 * adding i18next later is mechanical.
 */

export const MONEY = {
  list: {
    label: 'Valor de lista',
    chip: 'no es ingreso',
    help:
      'Tokens netos por el precio de lista. Es lo que valdría el trabajo a precio ' +
      'completo — no es dinero que haya entrado. Para eso, mira Efectivo.',
  },
  cash: {
    label: 'Efectivo',
    chip: 'piso',
    help:
      'Lo que Stripe liquidó de verdad. Es un PISO, no un total, mientras haya ' +
      'compras cuyo monto no quedó registrado: el ledger guarda TOKENS, y un ' +
      'cupón de 100% off los acredita igual que una compra pagada.',
  },
  contribution: {
    label: 'Contribución',
    chip: 'no incluye infra',
    help:
      'Efectivo menos el costo directo de Fargate medido. NO resta Aurora, ALB, ' +
      'NAT ni CloudWatch. No es margen.',
  },
  margin: {
    label: 'Margen',
    chip: '',
    help:
      'Contribución menos el costo de infraestructura del mes. Está indefinido ' +
      'mientras developers.infra_cost_monthly esté vacía — y vacía es la ' +
      'respuesta honesta, no cero.',
  },
} as const

/**
 * The sentences that EXPLAIN AN ABSENCE.
 *
 * They exist here for the same reason MONEY does: they are the only place in
 * the app allowed to name the things Fintela does not measure, and the eslint
 * gate fails CI on those words anywhere else. Putting them inline in a screen —
 * which is exactly how CI first caught this — makes the forbidden vocabulary
 * spreadable one component at a time.
 */
export const ABSENT = {
  noForecast:
    'No hay línea de pronóstico y no la habrá. El efectivo externo real es $0 — las ' +
    'compras históricas fueron cupones al 100% — y no existe modelo de suscripción. ' +
    'Una línea aquí sería un dibujo, no una proyección.',
  noRecurring:
    'Tampoco hay una cifra recurrente mensual ni anualizada, por la misma razón: no ' +
    'hay suscripciones que anualizar.',
  noRecurringTitle: 'Efectivo recurrente, y tasa de crecimiento',
  notCashLost:
    'No es efectivo perdido —nunca fue efectivo— pero sí es la señal de intención más ' +
    'clara que esta app puede ver.',
  forecastPanelTitle: 'Lo que no se puede proyectar',
} as const

export const DISCLAIMERS = {
  cashIsFloor: (n: number) =>
    `Piso, no total: ${n} compra${n === 1 ? '' : 's'} sin monto registrado. ` +
    `La columna token_ledger.usd_amount se añadió el 2026-08-28 sin backfill.`,
  marginUndefined:
    'developers.infra_cost_monthly está vacía. El margen no se puede calcular, ' +
    'y pintarlo como $0 diría que salimos tablas — que no es algo que nadie haya medido.',
  noForecast:
    'Aquí no hay proyección. El efectivo externo cobrado a la fecha es $0 y las ' +
    'cuatro compras históricas fueron cupones de 100% off: no se le puede ajustar ' +
    'una tasa de crecimiento a cero observaciones. Lo que sí se puede proyectar ' +
    'está en Costos y en Volumen de equilibrio.',
  noLoginHistory:
    'Keycloak tiene los eventos desactivados en producción, así que no existe ' +
    'historial de sesiones. "Activo" aquí significa que la organización hizo algo ' +
    'medible en la ventana — no que alguien haya entrado.',
  unmeasured: 'Sin medir — que no es lo mismo que cero.',
  storageEstimated:
    'El almacenamiento es estimado por coeficiente: el ORDEN entre organizaciones ' +
    'es exacto, el total va con unos puntos porcentuales de holgura.',
  internalOrgsExcluded:
    'Dos organizaciones internas concentran el 99.7% del almacenamiento. Sin ' +
    'excluirlas, cada agregado está mal por dos órdenes de magnitud.',
} as const

export const PROVENANCE_COPY = {
  LEDGER: { label: 'Ledger', help: 'Aurora, transaccional, exacto.' },
  MEASURED: { label: 'Medido', help: 'Aurora, estimado por coeficiente.' },
  EXTERNAL: { label: 'Externo', help: 'Foto de Stripe / Cost Explorer / Keycloak, con su fecha.' },
  MODELED: { label: 'Modelado', help: 'Calculado por nosotros a partir de supuestos que alguien tecleó.' },
} as const
